import { ComponentBase } from '@abstracts/component-base';
import { Component, Injector, Input, OnChanges, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import AnnotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-date-fns';

Chart.register(...registerables)

@Component({
  selector: 'energy-history-chart',
  templateUrl: './energy-history-chart.component.html',
  styleUrls: ['./energy-history-chart.component.scss']

})
export class EnergyHistoryChartComponent extends ComponentBase implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  public className: string = "EnergySourcesChart";

  @Input() isLoading: boolean = true;
  @Input() data: any
  @Input() dateSelected: Date

  public chart: Chart;
  public canvas: any;
  public ctx: any;
  public totalAmount: number;
  public max: number;
  public min: number;
  public average: number;
  public maxShow: boolean = false;
  public minShow: boolean = false;
  public isReadyForChart: boolean = false;
  public isShowingAverage: boolean = false;
  public defaultRange: string;

  constructor(
    injector: Injector
  ) {
    super(injector)
  }

  ngOnInit(): void {
    super.ngOnInit()
  }

  ngAfterViewInit() {
    this.isReadyForChart = true
  }

  ngOnChanges() {
    if (!this.data || !this.isReadyForChart) return

    if (!this.chart) this.createChart();
    else this.chart?.update();

    this.initDatasets();
  }

  ngOnDestroy() {
    this.chart?.destroy()
  }

  private initDatasets() {
    this.isLoading = true;

    try {
      this.chart.data.datasets = []
      if (!this.data || !this.data.some(entry => entry.dataPoints.length > 0)) {
        this.createEmptyChart();
        return;
      }

      console.log("this.data", this.data);
      for (const entry of this.data) {
        if (entry.dataPoints.length < 1)
          continue;

        if (!this.isEnergyMeterAliveAt(entry.energyMeter, this.dateSelected))
          continue;

        this.createChartDataset(entry);
        this.createChartDatasetLimiter(entry);
      }

      this.chart?.update()
    } catch (e) {

    } finally {
      this.isLoading = false;
    }
  }

  private isEnergyMeterAliveAt(energyMeter: any, dateTime: Date) {
    let lifetimeStartDate = new Date(energyMeter.lifetimeStart);
    lifetimeStartDate.setHours(0, 0, 0, 0);

    let lifetimeEndDate = new Date(energyMeter.lifetimeEnd);
    lifetimeEndDate.setHours(0, 0, 0, 0);

    let selectedDate = new Date(dateTime);
    selectedDate.setHours(0, 0, 0, 0);

    let isAfterStartDate = lifetimeStartDate <= selectedDate;
    let isBeforeEndDate = lifetimeEndDate > selectedDate;
    let isMeterAlive = energyMeter.isAlive;

    let result = isAfterStartDate && (isBeforeEndDate || isMeterAlive);

    return result;
  }

  private createChart() {
    this.canvas = document.getElementById('distribution-chart-canvas')
    this.ctx = this.canvas.getContext('2d')

    Chart.register(AnnotationPlugin)

    this.chart = new Chart(this.ctx, {

      plugins: [ChartDataLabels],
      type: 'line',
      data: {
        labels: [],
        datasets: [],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'minute',
              stepSize: 10,
              tooltipFormat: 'HH:mm',
              displayFormats: {
                millisecond: 'HH:mm',
                second: 'HH:mm',
                minute: 'HH:mm',
                hour: 'HH:mm',
                day: 'HH:mm',
                week: 'HH:mm',
                month: 'HH:mm',
                quarter: 'HH:mm',
                year: 'HH:mm',
              }
            },
          },
        },
        elements: {
          point: {
            radius: 0
          }
        },
        plugins: {
          filler: {
            propagate: false,
          },
          legend: {
            labels: {
              color: '#212529',
              usePointStyle: true,
              font: {
                size: 14,
                family: 'Lato',
              },
              filter: function (item, chart) {
                return item.text != "blind"
              }
            },
          },
          tooltip: {
            enabled: false,
            external: this.externalTooltipHandler.bind(this),
            position: 'nearest',
            intersect: false,
            axis: 'x',
            padding: 10,
            backgroundColor: 'rgba(0, 0, 0, .9)',
            animation: {
              duration: .1
            }
          }
        }
      }
    })

    this.isLoading = false
  }

  private createChartDataset(entry) {
    const color = this.getBackgroundColor(entry.energyMeter.energyMeterType.colorCodeInHex);

    // Erklärung: 1/1000 = 0.001 (Watt zu kW), -0.001showInverted 0.001notInverted
    const multiplier = entry.energyMeter.showInverted ? -0.001 : 0.001;
    const data = entry.dataPoints.map(record => {
      if (record == null) return null;
      return { x: new Date(record.utcTimestamp), y: record.value * multiplier };
    });

    var dataset = {
      label: entry.energyMeter.name,
      data: data,
      backgroundColor: `rgba(${color} / .2)`,
      borderColor: `rgb(${color})`
    };

    this.addDataset(dataset)
  }

  private createChartDatasetLimiter(entry) {
    if (entry.energyMeter.powerLimitInKw <= 0) return;

    const color = this.getBackgroundColor(entry.energyMeter.energyMeterType.colorCodeInHex);
    const firstTimestamp = entry.dataPoints[0].utcTimestamp;
    const lastTimestamp = entry.dataPoints[entry.dataPoints.length - 1].utcTimestamp; `
    `
    const x = new Date(entry.dataPoints[0].utcTimestamp);
    const data = [
      {
        x: x.setHours(0, 0, 0, 0),
        y: entry.energyMeter.powerLimitInKw
      },
      {
        x: x.setHours(23, 59, 59, 999),
        y: entry.energyMeter.powerLimitInKw
      }
    ];

    var dataset = {
      label: entry.energyMeter.name + "-Limit",
      data: data,
      backgroundColor: `rgba(${color} / .0)`,
      borderColor: `rgb(${color})`,
      borderWidth: 1,
      borderDash: [5, 5],
      pointStyle: 'line',
      hidden: true
    };

    this.addDataset(dataset);
  }

  private createEmptyChart() {
    const data = [
      {
        x: new Date(new Date().setHours(0, 0, 0, 0)),
        y: 0
      },
      {
        x: new Date(new Date().setHours(23, 59, 59, 999)),
        y: 0
      }
    ];

    var dataset = {
      label: "blind",
      data: data,
      borderWidth: 0
    };

    this.addDataset(dataset);
    this.chart?.update();
  }

  private addDataset(dataset) {
    if (!dataset['cubicInterpolationMode']) dataset['cubicInterpolationMode'] = 'monotone'
    if (!dataset['borderWidth']) dataset['borderWidth'] = 1
    if (!dataset['tension']) dataset['tension'] = 0.5

    dataset['fill'] = true
    dataset['datalabels'] = {}
    dataset['datalabels']['color'] = 'transparent'
    dataset['datalabels']['formatter'] = (value) => {
      if (isNaN(value)) return 0
      return value.y / 1000
    }

    const x = new Date(dataset.data[0].x);
    dataset.data.unshift({ x: x.setHours(0, 0, 0, 0), y: null })
    dataset.data.push({ x: x.setHours(23, 59, 59, 999), y: null })

    this.chart.data.datasets.push(dataset)
  }


  private getBackgroundColor(hex: string) {
    hex = hex.replace(/^#/, '');
    let bigint = parseInt(hex, 16);
    let r = (bigint >> 16) & 255;
    let g = (bigint >> 8) & 255;
    let b = bigint & 255;

    return `${r} ${g} ${b}`;
  }

  private externalTooltipHandler(context) {
    let tooltipEl = document.getElementById('chartjs-tooltip');

    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.id = 'chartjs-tooltip';
      tooltipEl.innerHTML = '<table></table>';
      document.body.appendChild(tooltipEl);
    }

    const tooltipModel = context.tooltip;
    if (tooltipModel.opacity === 0) {
      tooltipEl.style.opacity = '0';
      return;
    }

    tooltipEl.classList.remove('above', 'below', 'no-transform');
    if (tooltipModel.yAlign) {
      tooltipEl.classList.add(tooltipModel.yAlign);
    } else {
      tooltipEl.classList.add('no-transform');
    }

    function getBody(bodyItem) {
      return bodyItem.lines;
    }

    if (tooltipModel.body) {
      const titleLines = tooltipModel.title || [];
      const bodyLines = tooltipModel.body.map(getBody);

      let innerHtml = '<thead>';

      titleLines.forEach(function (title) {
        innerHtml += '<tr><th colspan="3">' + title + '</th></tr>';
      });
      innerHtml += '</thead><tbody>';

      bodyLines.forEach(function (body, i) {
        const split = body.toString().split(': ');
        const colors = tooltipModel.labelColors[i];
        const style = 'background:' + colors.backgroundColor + '; border: 1px solid ' + colors.borderColor;
        const marker = '<td><span style="' + style + '; border-radius: 100%; width: 12px; height: 12px; display: inline-block;"></span></td>';
        const label = '<td style="padding-left: 8px">' + split[0] + '</td>';
        const value = '<td style="padding-left: 8px"><strong>' + split[1] + ' kW</strong></td>';
        innerHtml += `<tr>${marker}${label}${value}</tr>`;
      });

      // Todo: Calculate self-sufficiency
      // const selfSufficiency = this.calculateSelfSufficiency(tooltipModel.dataPoints);
      // innerHtml += `<tr><td>Self-sufficiency: ${selfSufficiency}%</td></tr>`;

      innerHtml += '</tbody>';

      const tableRoot = tooltipEl.querySelector('table');
      tableRoot.innerHTML = innerHtml;
    }

    const position = this.chart.canvas.getBoundingClientRect();

    tooltipEl.style.background = '#212529';
    tooltipEl.style.color = 'white';
    tooltipEl.style.borderRadius = '0.25rem';
    tooltipEl.style.opacity = '1';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
    tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
    tooltipEl.style.font = tooltipModel.options.bodyFont.string;
    tooltipEl.style.padding = tooltipModel.options.padding + 'px ' + tooltipModel.options.padding * 1.5 + 'px';
    tooltipEl.style.pointerEvents = 'none';
  }

  // Todo
  private calculateSelfSufficiency(dataPoints) {
    let totalProduction = 0;
    let totalConsumption = 0;

    dataPoints.forEach(point => {
      if (point.datasetIndex === 0) {
        totalProduction += point.raw.y;
      } else if (point.datasetIndex === 1) {
        totalConsumption += point.raw.y;
      }
    });

    if (totalConsumption === 0) return 0;

    return ((totalProduction / totalConsumption) * 100).toFixed(2);
  }
}
